import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

/** Remove valores undefined (Firestore rejeita documentos com undefined). */
function stripUndefined(value) {
  if (value === undefined) return undefined;
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    return value.map((item) => stripUndefined(item));
  }
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value;
  const out = {};
  for (const [key, val] of Object.entries(value)) {
    if (val !== undefined) {
      out[key] = stripUndefined(val);
    }
  }
  return out;
}

/**
 * Registra uma ação no sistema de logs
 * @param {Object} logData - Dados do log
 * @param {string} logData.action - Ação realizada (CRIAR, ATUALIZAR, EXCLUIR, ENTRAR, SAIR, ATRIBUIR, RETIRAR, DEVOLVER)
 * @param {string} logData.module - Módulo do sistema (Propriedades, Blog, Usuarios, Configuracoes, Autenticacao)
 * @param {string} logData.type - Tipo específico (Login, Usuarios, Perfis, etc)
 * @param {string} logData.description - Descrição detalhada da ação
 * @param {string} logData.userId - ID do usuário que realizou a ação
 * @param {string} logData.userName - Nome do usuário que realizou a ação
 * @param {string} logData.userEmail - Email do usuário que realizou a ação
 * @param {string} [logData.targetId] - ID do item afetado (opcional)
 * @param {string} [logData.targetName] - Nome do item afetado (opcional)
 * @param {Object} [logData.metadata] - Dados adicionais (opcional)
 */
export const logAction = async (logData) => {
  try {
    // Obter IP do usuário (apenas simulação, pois no frontend não temos acesso ao IP real)
    const ip = await getUserIP();
    
    const logEntry = stripUndefined({
      action: logData.action,
      module: logData.module,
      type: logData.type,
      description: logData.description,
      userId: logData.userId,
      userName: logData.userName,
      userEmail: logData.userEmail,
      targetId: logData.targetId || null,
      targetName: logData.targetName || null,
      metadata: logData.metadata || {},
      ip: ip,
      timestamp: serverTimestamp(),
      createdAt: new Date()
    });

    // Adiciona o log na coleção systemLogs
    await addDoc(collection(db, 'systemLogs'), logEntry);
  } catch (error) {
    console.error('Erro ao registrar log:', error);
    // Não propaga o erro para não interromper a operação principal
  }
};

/**
 * Obtém o IP do usuário (simulação via API externa)
 */
const getUserIP = async () => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error('Erro ao obter IP:', error);
    return 'Desconhecido';
  }
};

/**
 * Labels legíveis para campos comuns do sistema.
 * Campos não listados aqui são ignorados no diff para não poluir os logs.
 */
const FIELD_LABELS = {
  title: 'Título',
  price: 'Preço',
  type: 'Tipo',
  condition: 'Condição',
  propertyStatus: 'Status',
  address: 'Endereço',
  street: 'Rua',
  complement: 'Complemento',
  neighborhood: 'Bairro',
  city: 'Cidade',
  state: 'Estado',
  zipCode: 'CEP',
  area: 'Área (m²)',
  landArea: 'Área do terreno (m²)',
  bedrooms: 'Quartos',
  bathrooms: 'Banheiros',
  suites: 'Suítes',
  garageSpaces: 'Vagas de garagem',
  yearBuilt: 'Ano de construção',
  iptu: 'IPTU',
  condoFee: 'Condomínio',
  isAvailable: 'Disponível',
  isFeatured: 'Destaque',
  showInHome: 'Exibir na Home',
  showFinancingSimulator: 'Simulador de financiamento',
  isReserved: 'Reservado',
  soldOrRented: 'Vendido/Alugado',
  isCancelled: 'Cancelado',
  createdBy: 'Criado por',
  assignedTo: 'Atribuído a',
  code: 'Código',
  name: 'Nome',
  email: 'E-mail',
  phone: 'Telefone',
  whatsapp: 'WhatsApp',
  cpfCnpj: 'CPF/CNPJ',
  category: 'Categoria',
  tags: 'Tags',
  slug: 'Slug',
  isPublished: 'Publicado',
  source: 'Origem',
  observation: 'Observação',
  value: 'Valor',
  amount: 'Valor',
  entryCode: 'Código do lançamento',
  order: 'Ordem',
  enabled: 'Habilitado',
  headline: 'Título principal',
  subHeadline: 'Subtítulo',
  ctaText: 'Texto do botão',
  ctaLink: 'Link do botão',
  authorName: 'Autor',
  rating: 'Avaliação',
  testimonial: 'Depoimento',
  condominiumPropertiesCount: 'Qtd. unidades',
  condominiumAvgUnitPrice: 'Preço médio por unidade',
  role: 'Perfil',
  tag: 'Tag',
  profileId: 'Perfil vinculado',
  isAdmin: 'Administrador',
  isActive: 'Ativo',
  number: 'Número',
  location: 'Localização',
  propertyId: 'Imóvel vinculado',
  withWhom: 'Com quem está',
  permissions: 'Permissões',
  subtitle: 'Subtítulo',
  ctaLabel: 'Texto do botão',
  heroImageUrl: 'Imagem principal',
  content: 'Conteúdo',
  favourite: 'Favorito',
  priority: 'Prioridade',
  date: 'Data',
  completed: 'Concluída',
  recurrence: 'Recorrência',
  propertyName: 'Imóvel',
  propertyInterest: 'Interesse',
  lossReason: 'Motivo de perda',
  leadHeatOverride: 'Calor do lead',
  descricao: 'Descrição',
  adminNotes: 'Obs. administrativas',
  visitDate: 'Data da visita',
  visitTime: 'Hora da visita',
  visitAddress: 'Endereço da visita',
  color: 'Cor',
  displayName: 'Nome de exibição',
  username: 'Usuário (@)',
  bio: 'Bio',
  creci: 'CRECI',
  status: 'Status',
  comment: 'Comentário',
  clientSince: 'Cliente desde',
  propertyCode: 'Código do imóvel',
  propertyTitle: 'Título do imóvel',
  notes: 'Notas',
  corretorAtendendo: 'Corretor atendendo',
  entryType: 'Tipo de lançamento',
  dueDate: 'Data de vencimento',
  paidAt: 'Data de pagamento',
  description: 'Descrição',
  template: 'Modelo',
  targetType: 'Tipo de página',
  contentSource: 'Origem do conteúdo',
  videoUrl: 'Vídeo',
  logoUrl: 'Logotipo',
  condominiumId: 'Condomínio vinculado',
  condominiumCode: 'Código do condomínio',
  clientName: 'Cliente',
  brokerId: 'Corretor',
  saleValue: 'Valor da venda',
  commissionPercent: 'Percentual de comissão',
  commissionValue: 'Valor da comissão',
  grossValue: 'Valor bruto',
  netValue: 'Valor líquido',
  expectedDate: 'Data prevista',
  receivedDate: 'Data de recebimento',
  paymentMethod: 'Forma de pagamento',
  observations: 'Observações',
  installmentsEnabled: 'Parcelamento'
};

const ROLE_LABELS = {
  admin: 'Administrador',
  broker: 'Corretor',
  client: 'Cliente'
};

const TAG_LABELS = {
  ADM: 'Administrador',
  BROKER: 'Corretor',
  CLI: 'Cliente'
};

/**
 * Normaliza valor para comparação no diff.
 */
function normalizeDiffValue(value) {
  if (value == null) return '';
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'object' && typeof value.toDate === 'function') {
    try {
      return value.toDate().toISOString();
    } catch {
      return String(value);
    }
  }
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  if (Array.isArray(value)) {
    const parts = value.map((item) => {
      if (item == null) return '';
      if (typeof item === 'object') {
        return item.code || item.title || item.name || item.label || item.id || '';
      }
      return String(item).trim();
    }).filter(Boolean);
    return parts.join(', ');
  }
  if (typeof value === 'object') {
    const extracted = value.code || value.title || value.name || value.label || value.id;
    return extracted != null ? String(extracted).trim() : null;
  }
  return String(value).trim();
}

const DIFF_IGNORE_FIELDS = new Set([
  'id',
  'updatedAt',
  'createdAt',
  'views',
  'lastViewedAt',
  'history',
  'stageHistory',
  'images',
  'floorPlans',
  'coverImage',
  'listingLogo',
  'coordinates',
  'seo',
  'tour360',
  'details',
  'condoDetails',
  'detailsCustom',
  'condoDetailsCustom',
  'watermarkApplied',
  'metadata',
  'links',
  'galleryUrls',
  'propertyFeatures',
  'condoFeatures',
  'propertyDetails',
  'map',
  'seo',
  'whatsappButton',
  'address',
  'rules',
  'stageHistory',
  'history',
  'permissions'
]);

const USER_REFERENCE_FIELDS = new Set([
  'brokerId',
  'createdBy',
  'assignedTo',
  'corretorAtendendo'
]);

const PROPERTY_TYPE_LABELS = {
  house: 'Casa',
  apartment: 'Apartamento',
  land: 'Terreno',
  loft: 'Loft',
  commercial: 'Comercial',
  rural: 'Rural',
  kitnet: 'Kitnet',
  studio: 'Estúdio',
  warehouse: 'Galpão',
  store: 'Loja',
  building: 'Prédio',
  office: 'Sala',
  farm: 'Sítio',
  countryHouse: 'Chácara'
};

function deriveCondominiumStatusLabel(data = {}) {
  if (data.isCancelled) return 'Cancelado';
  if (data.soldOrRented) return 'Vendido/Alugado';
  if (data.isAvailable) return 'Disponível';
  return 'Indisponível';
}

function derivePropertyStatusLabel(data = {}) {
  if (data.propertyStatus) return data.propertyStatus;
  if (data.isCancelled) return 'Cancelado';
  if (data.soldOrRented) return 'Vendido/Alugado';
  if (data.isReserved) return 'Reservado';
  if (data.isAvailable) return 'Disponível';
  return 'Indisponível';
}

const PROPERTY_PURPOSE_LABELS = {
  sale: 'Venda',
  rent: 'Aluguel'
};

const PROPERTY_HEAVY_FIELDS = [
  'description',
  'imageUrl',
  'heroImageUrl',
  'listingLogo',
  'videoUrl'
];

const PROPERTY_HEAVY_FIELD_LABELS = {
  description: 'Descrição',
  imageUrl: 'Imagem principal',
  heroImageUrl: 'Imagem principal',
  listingLogo: 'Logotipo do anúncio',
  videoUrl: 'Vídeo'
};

const PROPERTY_FIELD_LABELS = {
  purpose: 'Finalidade',
  propertyStatus: 'Status',
  displayTag: 'Tag de exibição',
  condominiumId: 'Condomínio vinculado',
  showInHome: 'Exibir na Home',
  showFinancingSimulator: 'Simulador de financiamento',
  isPreLaunch: 'Pré-lançamento',
  isLaunch: 'Lançamento'
};

const LANDING_TEMPLATE_LABELS = {
  capture: 'Template 1 — Foco em captação',
  visual: 'Template 2 — Foco visual',
  luxury: 'Template 3 — Alto padrão'
};

const LANDING_TARGET_TYPE_LABELS = {
  home: 'Página principal (Home)',
  property: 'Imóvel específico',
  condominium: 'Condomínio específico'
};

const LANDING_CONTENT_SOURCE_LABELS = {
  manual: 'Preenchimento manual',
  home_settings: 'Dados da página principal',
  property: 'Dados do imóvel',
  condominium: 'Dados do condomínio'
};

const LANDING_STATUS_LABELS = {
  draft: 'Rascunho',
  published: 'Publicada'
};

const BLOG_ROBOTS_INDEX_LABELS = {
  index: 'Indexar',
  noindex: 'Não indexar'
};

const BLOG_TWITTER_CARD_LABELS = {
  summary_large_image: 'Imagem grande',
  summary: 'Resumo'
};

const BLOG_HEAVY_FIELDS = [
  'content',
  'conclusion',
  'imageUrl',
  'image2Url',
  'image3Url',
  'seoImageUrl',
  'ogImageUrl',
  'twitterImageUrl'
];

const BLOG_HEAVY_FIELD_LABELS = {
  content: 'Conteúdo',
  conclusion: 'Conclusão',
  imageUrl: 'Imagem 1',
  image2Url: 'Imagem 2',
  image3Url: 'Imagem 3',
  seoImageUrl: 'Imagem SEO',
  ogImageUrl: 'Imagem Open Graph',
  twitterImageUrl: 'Imagem Twitter'
};

const BLOG_FIELD_LABELS = {
  seoMetaTitle: 'Meta título (SEO)',
  seoMetaDescription: 'Meta descrição (SEO)',
  seoKeywords: 'Palavras-chave (SEO)',
  seoKeywordsSecondary: 'Palavras-chave secundárias (SEO)',
  useCoverImageForSeo: 'Usar capa como imagem SEO',
  schemaType: 'Tipo de schema',
  schemaFaq: 'Schema FAQ',
  schemaBreadcrumb: 'Schema breadcrumb',
  ogTitle: 'Título Open Graph',
  ogDescription: 'Descrição Open Graph',
  ogImageAlt: 'Texto alternativo OG',
  twitterCard: 'Card Twitter',
  twitterTitle: 'Título Twitter',
  twitterDescription: 'Descrição Twitter',
  twitterImageAlt: 'Texto alternativo Twitter',
  robotsIndex: 'Indexação (robots)',
  robotsFollow: 'Seguir links (robots)',
  robotsArchive: 'Arquivar (robots)',
  redirect301OnSlugChange: 'Redirecionamento 301 ao mudar slug',
  canonicalUrl: 'URL canônica'
};

const AGENDA_PRIORITY_LABELS = {
  baixa: 'Baixa',
  media: 'Média',
  alta: 'Alta'
};

const AGENDA_RECURRENCE_LABELS = {
  none: 'Não repetir',
  daily: 'Repetir por X dias',
  weekly: 'Toda semana',
  biweekly: 'A cada 14 dias',
  monthly: 'Todo mês',
  everyXdays: 'A cada X dias'
};

const LEAD_TYPE_LABELS = {
  help: 'Ajuda',
  broker: 'Falar com corretor',
  sell: 'Vender imóvel',
  buy: 'Comprar imóvel',
  rent: 'Alugar imóvel',
  interest: 'Interesse em imóvel',
  support: 'Suporte técnico',
  avaliacao: 'Avaliação',
  outros: 'Outros'
};

const LEAD_STATUS_LABELS = {
  novo: 'Novo',
  qualificado: 'Qualificado',
  proposta: 'Proposta enviada',
  negociacao: 'Negociação',
  ganho: 'Ganho',
  perdido: 'Perdido'
};

const LEAD_SOURCE_LABELS = {
  pipeline: 'Pipeline',
  manual: 'Cadastro manual',
  chat: 'Chat',
  landing: 'Landing page',
  property: 'Página do imóvel',
  contact: 'Formulário de contato',
  contact_form: 'Formulário de contato',
};

const FIELD_VALUE_LABELS = {
  template: LANDING_TEMPLATE_LABELS,
  targetType: LANDING_TARGET_TYPE_LABELS,
  contentSource: LANDING_CONTENT_SOURCE_LABELS,
  status: { ...LANDING_STATUS_LABELS, ...LEAD_STATUS_LABELS },
  priority: AGENDA_PRIORITY_LABELS,
  recurrence: AGENDA_RECURRENCE_LABELS,
  type: LEAD_TYPE_LABELS,
  source: LEAD_SOURCE_LABELS,
  robotsIndex: BLOG_ROBOTS_INDEX_LABELS,
  twitterCard: BLOG_TWITTER_CARD_LABELS,
  purpose: PROPERTY_PURPOSE_LABELS
};

/** Rótulos em português para exibição de módulos/tipos na tela de logs. */
const LOG_TYPE_LABELS = {
  LandingPages: 'Páginas de destino',
  Lancamentos: 'Lançamentos financeiros',
  LinkBio: 'Link na bio',
  Pipeline: 'Funil de vendas',
  Login: 'Autenticação',
  Usuarios: 'Usuários',
  Perfis: 'Perfis',
  Propriedades: 'Propriedades',
  Condominios: 'Condomínios',
  Blog: 'Blog',
  Categorias: 'Categorias do blog',
  Leads: 'Leads',
  Chaves: 'Chaves',
  Tarefas: 'Tarefas',
  Eventos: 'Eventos',
  Notas: 'Notas',
  Configuracoes: 'Configurações',
  Depoimentos: 'Depoimentos',
  Filiais: 'Filiais',
  'Motivos de Perda': 'Motivos de perda',
  'Mensagens CRM': 'Mensagens do CRM',
  Financeiro: 'Financeiro',
  Agenda: 'Agenda',
  Autenticacao: 'Autenticação'
};

export function translateLandingStatus(status) {
  return LANDING_STATUS_LABELS[status] || status || '(vazio)';
}

export function translateBlogStatus(status) {
  return translateLandingStatus(status);
}

/**
 * Diff legível para posts do blog (evita HTML/URLs enormes no log).
 */
export function buildBlogChangeDiff(oldData, newData) {
  if (!oldData || !newData) return [];

  const strippedOld = { ...oldData };
  const strippedNew = { ...newData };
  const heavyDiff = [];

  for (const key of BLOG_HEAVY_FIELDS) {
    const oldVal = normalizeDiffValue(oldData[key]);
    const newVal = normalizeDiffValue(newData[key]);
    if (oldVal === newVal) continue;

    const isImage = key.includes('Url') || key.includes('Image');
    heavyDiff.push({
      field: key,
      label: BLOG_HEAVY_FIELD_LABELS[key] || key,
      before: oldVal ? (isImage ? '(com imagem)' : '(com conteúdo)') : '(vazio)',
      after: newVal ? (isImage ? '(com imagem)' : '(com conteúdo)') : '(vazio)'
    });
    delete strippedOld[key];
    delete strippedNew[key];
  }

  return [...buildChangeDiff(strippedOld, strippedNew, BLOG_FIELD_LABELS), ...heavyDiff];
}

/**
 * Diff legível para imóveis (evita descrições e URLs enormes no log).
 */
export function buildPropertyChangeDiff(oldData, newData, valueResolvers = {}) {
  if (!oldData || !newData) return [];

  const strippedOld = { ...oldData };
  const strippedNew = { ...newData };
  const heavyDiff = [];

  for (const key of PROPERTY_HEAVY_FIELDS) {
    const oldVal = normalizeDiffValue(oldData[key]);
    const newVal = normalizeDiffValue(newData[key]);
    if (oldVal === newVal) continue;

    const isImage = key.includes('Url') || key.includes('Logo') || key.includes('Image');
    const isVideo = key === 'videoUrl';
    heavyDiff.push({
      field: key,
      label: PROPERTY_HEAVY_FIELD_LABELS[key] || key,
      before: oldVal
        ? (isVideo ? '(com vídeo)' : isImage ? '(com imagem)' : '(com conteúdo)')
        : '(vazio)',
      after: newVal
        ? (isVideo ? '(com vídeo)' : isImage ? '(com imagem)' : '(com conteúdo)')
        : '(vazio)'
    });
    delete strippedOld[key];
    delete strippedNew[key];
  }

  return [
    ...buildChangeDiff(strippedOld, strippedNew, PROPERTY_FIELD_LABELS, valueResolvers),
    ...heavyDiff
  ];
}

export function getLogTypeLabel(type) {
  if (!type) return 'N/A';
  return LOG_TYPE_LABELS[type] || type;
}

function resolveFieldDisplayValue(key, val, valueResolvers = {}) {
  const raw = val || '(vazio)';
  if (raw === '(vazio)') return raw;
  if (typeof valueResolvers[key] === 'function') {
    const resolved = valueResolvers[key](raw);
    if (resolved) return resolved;
  }
  if (FIELD_VALUE_LABELS[key]?.[raw]) return FIELD_VALUE_LABELS[key][raw];
  if (key === 'type' && PROPERTY_TYPE_LABELS[raw]) return PROPERTY_TYPE_LABELS[raw];
  if (key === 'role' && ROLE_LABELS[raw]) return ROLE_LABELS[raw];
  if (key === 'tag' && TAG_LABELS[raw]) return TAG_LABELS[raw];
  return raw;
}

/**
 * Compara dois objetos e retorna as diferenças em campos relevantes.
 * valueResolvers: { campo: (rawId) => 'Nome legível' }
 */
export function buildChangeDiff(oldData, newData, customLabels = {}, valueResolvers = {}) {
  if (!oldData || !newData) return [];
  const labels = { ...FIELD_LABELS, ...customLabels };
  const diff = [];

  const formatDisplay = (key, val) => resolveFieldDisplayValue(key, val, valueResolvers);

  const allKeys = new Set([
    ...Object.keys(labels),
    ...Object.keys(oldData),
    ...Object.keys(newData)
  ]);

  for (const key of allKeys) {
    if (DIFF_IGNORE_FIELDS.has(key)) continue;
    if (!labels[key] && !(key in oldData) && !(key in newData)) continue;

    const oldVal = normalizeDiffValue(oldData[key]);
    const newVal = normalizeDiffValue(newData[key]);

    if (oldVal === null || newVal === null) continue;
    if (oldVal === newVal) continue;

    diff.push({
      field: key,
      label: labels[key] || key,
      before: formatDisplay(key, oldVal),
      after: formatDisplay(key, newVal)
    });
  }

  return diff;
}

/**
 * Formata entrada do diff para exibição (resolve IDs de usuário já salvos).
 */
export function formatChangeDiffEntry(change, userLookup = {}) {
  const valueResolvers = {};
  if (USER_REFERENCE_FIELDS.has(change.field)) {
    valueResolvers[change.field] = (val) => userLookup[val] || val;
  }
  const format = (val) => resolveFieldDisplayValue(change.field, val, valueResolvers);

  return {
    ...change,
    before: format(change.before),
    after: format(change.after)
  };
}

/**
 * Converte formato legado { campo: { from, to } } para changeDiff.
 */
export function changesFromToToDiff(changes, labelMap = {}) {
  if (!changes || typeof changes !== 'object' || Array.isArray(changes)) return [];
  return Object.entries(changes)
    .filter(([, val]) => val && typeof val === 'object' && ('from' in val || 'to' in val))
    .map(([field, val]) => ({
      field,
      label: labelMap[field] || FIELD_LABELS[field] || field,
      before: val.from == null || val.from === '' ? '(vazio)' : String(val.from),
      after: val.to == null || val.to === '' ? '(vazio)' : String(val.to)
    }));
}

/**
 * Converte array de strings "Campo: antes → depois" para changeDiff.
 */
export function stringChangesToDiff(changes) {
  if (!Array.isArray(changes)) return [];
  return changes.map((str) => {
    const s = String(str);
    const arrow = s.match(/^(.+?):\s*(.+?)\s*→\s*(.+)$/);
    if (arrow) {
      return {
        field: arrow[1].trim(),
        label: arrow[1].trim(),
        before: arrow[2].trim(),
        after: arrow[3].trim()
      };
    }
    return { field: 'change', label: 'Alteração', before: '-', after: s };
  });
}

/**
 * Diff de um único campo (ex.: mudança de status).
 */
export function buildSingleChangeDiff(field, label, before, after) {
  const b = before == null || before === '' ? '(vazio)' : String(before);
  const a = after == null || after === '' ? '(vazio)' : String(after);
  if (b === a) return [];
  return [{ field, label, before: b, after: a }];
}

/**
 * Extrai o diff de alterações de um log (suporta formatos antigos e novos).
 */
export function getLogChangeDiff(log) {
  const metadata = log?.metadata || {};
  if (Array.isArray(metadata.changeDiff) && metadata.changeDiff.length > 0) {
    return metadata.changeDiff;
  }
  if (Array.isArray(metadata.changes?.changeDiff) && metadata.changes.changeDiff.length > 0) {
    return metadata.changes.changeDiff;
  }
  if (metadata.changes && typeof metadata.changes === 'object' && !Array.isArray(metadata.changes)) {
    const nested = changesFromToToDiff(metadata.changes);
    if (nested.length > 0) return nested;
    const fromTo = changesFromToToDiff(metadata.changes.changes || metadata.changes);
    if (fromTo.length > 0) return fromTo;
  }
  if (Array.isArray(metadata.changes) && metadata.changes.length > 0) {
    const first = metadata.changes[0];
    if (typeof first === 'string') {
      return stringChangesToDiff(metadata.changes);
    }
  }
  if (metadata.newStatus != null && metadata.oldStatus != null) {
    return buildSingleChangeDiff('status', 'Status', metadata.oldStatus, metadata.newStatus);
  }
  if (metadata.newStatus != null && log?.action === 'ATUALIZAR') {
    return buildSingleChangeDiff('status', 'Status', '(anterior)', metadata.newStatus);
  }
  return [];
}

/**
 * Funções auxiliares para registrar ações específicas
 */

// Logs de Propriedades
export const logPropertyCreate = (user, propertyData) => {
  const typeLabel = PROPERTY_TYPE_LABELS[propertyData.type] || propertyData.type || 'N/A';
  const purposeLabel = PROPERTY_PURPOSE_LABELS[propertyData.purpose] || propertyData.purpose || 'N/A';
  return logAction({
    action: 'CRIAR',
    module: 'Propriedades',
    type: 'Propriedades',
    description: `Novo imóvel cadastrado: "${propertyData.title || 'Sem título'}" (${typeLabel})`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: propertyData.id,
    targetName: propertyData.title,
    metadata: {
      type: typeLabel,
      purpose: purposeLabel,
      price: propertyData.price,
      status: derivePropertyStatusLabel(propertyData)
    }
  });
};

export const logPropertyDuplicate = (user, sourceProperty, newProperty) => {
  const typeLabel = PROPERTY_TYPE_LABELS[newProperty.type] || newProperty.type || 'N/A';
  return logAction({
    action: 'CRIAR',
    module: 'Propriedades',
    type: 'Propriedades',
    description: `Imóvel duplicado: "${newProperty.title || 'Sem título'}" a partir de "${sourceProperty.title || sourceProperty.id}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: newProperty.id,
    targetName: newProperty.title,
    metadata: {
      sourceId: sourceProperty.id,
      sourceTitle: sourceProperty.title,
      type: typeLabel,
      price: newProperty.price
    }
  });
};

export const logPropertyUpdate = (user, propertyData, changes = {}) => {
  return logAction({
    action: 'ATUALIZAR',
    module: 'Propriedades',
    type: 'Propriedades',
    description: `Imóvel atualizado: "${propertyData.title || 'Sem título'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: propertyData.id,
    targetName: propertyData.title,
    metadata: { ...changes }
  });
};

export const logPropertyDelete = (user, propertyData) => {
  const typeLabel = PROPERTY_TYPE_LABELS[propertyData.type] || propertyData.type || 'N/A';
  return logAction({
    action: 'EXCLUIR',
    module: 'Propriedades',
    type: 'Propriedades',
    description: `Imóvel excluído: "${propertyData.title || 'Sem título'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: propertyData.id,
    targetName: propertyData.title,
    metadata: {
      type: typeLabel,
      price: propertyData.price,
      status: derivePropertyStatusLabel(propertyData)
    }
  });
};

export const logPropertyBulkDelete = (user, count) => {
  return logAction({
    action: 'EXCLUIR',
    module: 'Propriedades',
    type: 'Propriedades',
    description: `${count} imóvel(is) excluído(s)`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    metadata: { count }
  });
};

// Logs de Blog
export const logBlogCreate = (user, postData) => {
  return logAction({
    action: 'CRIAR',
    module: 'Blog',
    type: 'Blog',
    description: `Nova postagem criada: "${postData.title || 'Sem título'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: postData.id,
    targetName: postData.title,
    metadata: {
      status: translateBlogStatus(postData.status),
      category: postData.category || '(sem categoria)'
    }
  });
};

export const logBlogUpdate = (user, postData, changes = {}) => {
  return logAction({
    action: 'ATUALIZAR',
    module: 'Blog',
    type: 'Blog',
    description: `Postagem atualizada: "${postData.title || 'Sem título'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: postData.id,
    targetName: postData.title,
    metadata: { ...changes }
  });
};

export const logBlogDelete = (user, postData) => {
  return logAction({
    action: 'EXCLUIR',
    module: 'Blog',
    type: 'Blog',
    description: `Postagem excluída: "${postData.title || 'Sem título'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: postData.id,
    targetName: postData.title
  });
};

export const logBlogPublish = (user, postData, newStatus, oldStatus) => {
  const statusLabel = newStatus === 'published' ? 'publicada' : 'despublicada';
  const changeDiff = buildSingleChangeDiff(
    'status',
    'Status',
    translateBlogStatus(oldStatus),
    translateBlogStatus(newStatus)
  );
  return logAction({
    action: 'ATUALIZAR',
    module: 'Blog',
    type: 'Blog',
    description: `Postagem ${statusLabel}: "${postData.title || 'Sem título'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: postData.id,
    targetName: postData.title,
    metadata: {
      newStatus: translateBlogStatus(newStatus),
      changeDiff
    }
  });
};

export const logBlogCategoryCreate = (user, categoryName) => {
  return logAction({
    action: 'CRIAR',
    module: 'Blog',
    type: 'Categorias',
    description: `Categoria criada: "${categoryName}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetName: categoryName
  });
};

export const logBlogCategoryDelete = (user, categoryName) => {
  return logAction({
    action: 'EXCLUIR',
    module: 'Blog',
    type: 'Categorias',
    description: `Categoria excluída: "${categoryName}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetName: categoryName
  });
};

// Logs de Usuários
export const logUserCreate = (adminUser, newUserData) => {
  const roleLabel = ROLE_LABELS[newUserData.role] || newUserData.role || 'N/A';
  return logAction({
    action: 'CRIAR',
    module: 'Usuarios',
    type: 'Usuarios',
    description: `Novo usuário cadastrado: "${newUserData.name || newUserData.email}" (${newUserData.email})`,
    userId: adminUser.uid,
    userName: adminUser.displayName || adminUser.name || adminUser.email,
    userEmail: adminUser.email,
    targetId: newUserData.id,
    targetName: newUserData.name || newUserData.email,
    metadata: {
      role: roleLabel,
      tag: TAG_LABELS[newUserData.tag] || newUserData.tag,
      isAdmin: newUserData.isAdmin ? 'Sim' : 'Não'
    }
  });
};

export const logUserUpdate = (adminUser, userData, changes = {}) => {
  return logAction({
    action: 'ATUALIZAR',
    module: 'Usuarios',
    type: 'Usuarios',
    description: `Usuário atualizado: "${userData.name || userData.email}"`,
    userId: adminUser.uid,
    userName: adminUser.displayName || adminUser.name || adminUser.email,
    userEmail: adminUser.email,
    targetId: userData.id,
    targetName: userData.name || userData.email,
    metadata: { ...changes }
  });
};

export const logUserDelete = (adminUser, userData) => {
  const roleLabel = ROLE_LABELS[userData.role] || userData.role || 'N/A';
  return logAction({
    action: 'EXCLUIR',
    module: 'Usuarios',
    type: 'Usuarios',
    description: `Usuário excluído: "${userData.name || userData.email}"`,
    userId: adminUser.uid,
    userName: adminUser.displayName || adminUser.name || adminUser.email,
    userEmail: adminUser.email,
    targetId: userData.id,
    targetName: userData.name || userData.email,
    metadata: {
      email: userData.email,
      role: roleLabel,
      tag: TAG_LABELS[userData.tag] || userData.tag
    }
  });
};

// Logs de Autenticação
export const logUserLogin = (user) => {
  return logAction({
    action: 'ENTRAR',
    module: 'Autenticacao',
    type: 'Login',
    description: `Usuário ${user.displayName || user.name || user.email} realizou login no sistema`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email
  });
};

export const logUserLogout = (user) => {
  return logAction({
    action: 'SAIR',
    module: 'Autenticacao',
    type: 'Login',
    description: `Usuário ${user.displayName || user.name || user.email} realizou logout do sistema`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email
  });
};

// Logs de Configurações
export const logSettingsUpdate = (user, section, changes = {}) => {
  return logAction({
    action: 'ATUALIZAR',
    module: 'Configuracoes',
    type: 'Configuracoes',
    description: `Configurações atualizadas na seção: ${section}`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    metadata: { section, ...changes }
  });
};

// Logs de Colunas do Pipeline
export const logPipelineColumnCreate = (user, columnData) => {
  return logAction({
    action: 'CRIAR',
    module: 'Configuracoes',
    type: 'Pipeline',
    description: `Coluna criada no pipeline: "${columnData.title || 'Sem título'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: columnData.id,
    targetName: columnData.title,
    metadata: {
      color: columnData.color,
      order: columnData.order,
      fixed: columnData.fixed
    }
  });
};

export const logPipelineColumnUpdate = (user, columnData, changes = {}) => {
  // Construir descrição detalhada das mudanças
  const changeDescriptions = [];
  const changeDiff = changes.changeDiff || changesFromToToDiff(changes);
  
  if (changes.title) {
    changeDescriptions.push(`Nome alterado de "${changes.title.from}" para "${changes.title.to}"`);
  }
  
  if (changes.color) {
    const fromColor = changes.color.from === 'padrão' ? 'cor padrão' : `cor ${changes.color.from}`;
    const toColor = changes.color.to === 'padrão' ? 'cor padrão' : `cor ${changes.color.to}`;
    changeDescriptions.push(`Cor alterada de ${fromColor} para ${toColor}`);
  }
  
  const description = changeDescriptions.length > 0 
    ? `Coluna "${columnData.title || 'Sem título'}" atualizada: ${changeDescriptions.join(', ')}`
    : `Coluna atualizada no pipeline: "${columnData.title || 'Sem título'}"`;
  
  return logAction({
    action: 'ATUALIZAR',
    module: 'Configuracoes',
    type: 'Pipeline',
    description: description,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: columnData.id,
    targetName: columnData.title,
    metadata: { ...changes, changeDiff }
  });
};

export const logPipelineColumnDelete = (user, columnData) => {
  return logAction({
    action: 'EXCLUIR',
    module: 'Configuracoes',
    type: 'Pipeline',
    description: `Coluna excluída do pipeline: "${columnData.title || 'Sem título'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: columnData.id,
    targetName: columnData.title,
    metadata: {
      color: columnData.color,
      order: columnData.order
    }
  });
};

// Logs de Perfis
export const logProfileCreate = (user, profileData) => {
  const activeCount = Object.values(profileData.permissions || {}).filter(Boolean).length;
  return logAction({
    action: 'CRIAR',
    module: 'Usuarios',
    type: 'Perfis',
    description: `Novo perfil cadastrado: "${profileData.name}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: profileData.id,
    targetName: profileData.name,
    metadata: {
      permissionsAtivas: activeCount
    }
  });
};

export const logProfileUpdate = (user, profileData, changes = {}) => {
  return logAction({
    action: 'ATUALIZAR',
    module: 'Usuarios',
    type: 'Perfis',
    description: `Perfil atualizado: "${profileData.name}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: profileData.id,
    targetName: profileData.name,
    metadata: { ...changes }
  });
};

export const logProfileDelete = (user, profileData) => {
  return logAction({
    action: 'EXCLUIR',
    module: 'Usuarios',
    type: 'Perfis',
    description: `Perfil excluído: "${profileData.name}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: profileData.id,
    targetName: profileData.name
  });
};

// Logs de Chaves
export const logKeyCreate = (user, keyData) => {
  return logAction({
    action: 'CRIAR',
    module: 'Chaves',
    type: 'Chaves',
    description: `Nova chave cadastrada: "${keyData.number || 'Sem número'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: keyData.id,
    targetName: keyData.number,
    metadata: {
      description: keyData.description,
      propertyName: keyData.propertyName || null,
      propertyId: keyData.propertyId,
      status: keyData.status
    }
  });
};

export const logKeyUpdate = (user, keyData, changes = {}) => {
  return logAction({
    action: 'ATUALIZAR',
    module: 'Chaves',
    type: 'Chaves',
    description: `Chave atualizada: "${keyData.number || 'Sem número'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: keyData.id,
    targetName: keyData.number,
    metadata: { ...changes }
  });
};

export const logKeyDelete = (user, keyData) => {
  return logAction({
    action: 'EXCLUIR',
    module: 'Chaves',
    type: 'Chaves',
    description: `Chave excluída: "${keyData.number || 'Sem número'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: keyData.id,
    targetName: keyData.number,
    metadata: {
      status: keyData.status
    }
  });
};

export const logKeyWithdraw = (user, keyData, brokerData) => {
  return logAction({
    action: 'RETIRAR',
    module: 'Chaves',
    type: 'Chaves',
    description: `Chave retirada: "${keyData.number}" por ${brokerData.name || 'Corretor'}`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: keyData.id,
    targetName: keyData.number,
    metadata: {
      brokerId: brokerData.id,
      brokerName: brokerData.name,
      expectedReturnDate: brokerData.expectedReturnDate
    }
  });
};

export const logKeyReturn = (user, keyData) => {
  return logAction({
    action: 'DEVOLVER',
    module: 'Chaves',
    type: 'Chaves',
    description: `Chave devolvida: "${keyData.number}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: keyData.id,
    targetName: keyData.number,
    metadata: {
      propertyCondition: keyData.propertyCondition
    }
  });
};

// Logs de Leads
export const logLeadCreate = (user, leadData) => {
  const typeLabel = LEAD_TYPE_LABELS[leadData.type] || leadData.type || 'N/A';
  const sourceLabel = LEAD_SOURCE_LABELS[leadData.source] || leadData.source || 'Chat';
  return logAction({
    action: 'CRIAR',
    module: 'Leads',
    type: 'Leads',
    description: `Novo lead criado: "${leadData.name || 'Sem nome'}" (${typeLabel})`,
    userId: user?.uid || 'system',
    userName: user?.displayName || user?.name || user?.email || 'Sistema',
    userEmail: user?.email || '',
    targetId: leadData.id,
    targetName: leadData.name,
    metadata: {
      type: typeLabel,
      phone: leadData.phone,
      email: leadData.email,
      source: sourceLabel
    }
  });
};

export const logLeadUpdate = (user, leadData, changes = {}) => {
  return logAction({
    action: 'ATUALIZAR',
    module: 'Leads',
    type: 'Leads',
    description: `Lead atualizado: "${leadData.name || 'Sem nome'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: leadData.id,
    targetName: leadData.name,
    metadata: { ...changes }
  });
};

export const logLeadAssignBroker = (user, leadData, brokerName) => {
  return logAction({
    action: 'ATRIBUIR',
    module: 'Leads',
    type: 'Leads',
    description: `Corretor atribuído ao lead "${leadData.name || 'Sem nome'}": ${brokerName || 'Não atribuído'}`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: leadData.id,
    targetName: leadData.name,
    metadata: {
      brokerName: brokerName || 'Não atribuído',
      previousBroker: leadData.previousBrokerName || 'Nenhum'
    }
  });
};

// Logs de Pipeline
export const logPipelineCreate = (user, cardData) => {
  const typeLabel = LEAD_TYPE_LABELS[cardData.type] || cardData.type || 'N/A';
  return logAction({
    action: 'CRIAR',
    module: 'Pipeline',
    type: 'Pipeline',
    description: `Novo card criado no pipeline: "${cardData.name || 'Sem nome'}"`,
    userId: user?.uid || 'system',
    userName: user?.displayName || user?.name || user?.email || 'Sistema',
    userEmail: user?.email || '',
    targetId: cardData.id,
    targetName: cardData.name,
    metadata: {
      status: cardData.status,
      type: typeLabel,
      phone: cardData.phone,
      email: cardData.email
    }
  });
};

export const logPipelineUpdate = (user, cardData, changes = {}) => {
  let changeDiff = changes.changeDiff;
  if (!changeDiff?.length) {
    if (Array.isArray(changes.changes) && changes.changes.length > 0) {
      changeDiff = stringChangesToDiff(changes.changes);
    } else {
      changeDiff = changesFromToToDiff(changes);
    }
  }
  return logAction({
    action: 'ATUALIZAR',
    module: 'Pipeline',
    type: 'Pipeline',
    description: `Card atualizado no pipeline: "${cardData.name || 'Sem nome'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: cardData.id,
    targetName: cardData.name,
    metadata: { ...changes, changeDiff }
  });
};

export const logPipelineDelete = (user, cardData) => {
  return logAction({
    action: 'EXCLUIR',
    module: 'Pipeline',
    type: 'Pipeline',
    description: `Card excluído do pipeline: "${cardData.name || 'Sem nome'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: cardData.id,
    targetName: cardData.name,
    metadata: {
      status: cardData.status,
      type: cardData.type
    }
  });
};

export const logPipelineMove = (user, cardData, fromStatus, toStatus) => {
  return logAction({
    action: 'MOVER',
    module: 'Pipeline',
    type: 'Pipeline',
    description: `Card movido no pipeline: "${cardData.name || 'Sem nome'}" de ${fromStatus} para ${toStatus}`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: cardData.id,
    targetName: cardData.name,
    metadata: {
      fromStatus: fromStatus,
      toStatus: toStatus
    }
  });
};

export const logPipelineLossReason = (user, cardData, lossReasonId, lossReasonName) => {
  return logAction({
    action: 'ATUALIZAR',
    module: 'Pipeline',
    type: 'Pipeline',
    description: `Motivo de perda atribuído ao card "${cardData.name || 'Sem nome'}": ${lossReasonName}`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: cardData.id,
    targetName: cardData.name,
    metadata: {
      lossReasonId: lossReasonId,
      lossReasonName: lossReasonName,
      status: 'perdido'
    }
  });
};

// Logs de Motivos de Perda
export const logLossReasonCreate = (user, reasonData) => {
  return logAction({
    action: 'CRIAR',
    module: 'Configuracoes',
    type: 'Motivos de Perda',
    description: `Motivo de perda criado: "${reasonData.name || 'Sem nome'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: reasonData.id,
    targetName: reasonData.name,
    metadata: {
      fixed: reasonData.fixed || false
    }
  });
};

export const logLossReasonUpdate = (user, oldReasonData, changes = {}) => {
  let description = `Motivo de perda atualizado: "${oldReasonData.name || 'Sem nome'}"`;
  const changeDetails = [];

  if (changes.name) {
    changeDetails.push(`Nome alterado de '${changes.name.from}' para '${changes.name.to}'`);
  }

  if (changeDetails.length > 0) {
    description += `: ${changeDetails.join(', ')}`;
  }

  return logAction({
    action: 'ATUALIZAR',
    module: 'Configuracoes',
    type: 'Motivos de Perda',
    description: description,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: oldReasonData.id,
    targetName: oldReasonData.name,
    metadata: { ...changes, changeDiff: changes.changeDiff || changesFromToToDiff(changes) }
  });
};

export const logLossReasonDelete = (user, reasonData) => {
  return logAction({
    action: 'EXCLUIR',
    module: 'Configuracoes',
    type: 'Motivos de Perda',
    description: `Motivo de perda excluído: "${reasonData.name || 'Sem nome'}" (ID: ${reasonData.id})`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: reasonData.id,
    targetName: reasonData.name,
    metadata: {
      fixed: reasonData.fixed || false
    }
  });
};

// Logs de Feedbacks/Depoimentos
export const logFeedbackCreate = (user, feedbackData) => {
  return logAction({
    action: 'CRIAR',
    module: 'Configuracoes',
    type: 'Depoimentos',
    description: `Novo depoimento cadastrado: "${feedbackData.name || 'Sem nome'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: feedbackData.id,
    targetName: feedbackData.name,
    metadata: {
      rating: feedbackData.rating,
      clientSince: feedbackData.clientSince
    }
  });
};

export const logFeedbackUpdate = (user, feedbackData, changes = {}) => {
  return logAction({
    action: 'ATUALIZAR',
    module: 'Configuracoes',
    type: 'Depoimentos',
    description: `Depoimento atualizado: "${feedbackData.name || 'Sem nome'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: feedbackData.id,
    targetName: feedbackData.name,
    metadata: { rating: feedbackData.rating, ...changes }
  });
};

export const logFeedbackDelete = (user, feedbackData) => {
  return logAction({
    action: 'EXCLUIR',
    module: 'Configuracoes',
    type: 'Depoimentos',
    description: `Depoimento excluído: "${feedbackData.name || 'Sem nome'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: feedbackData.id,
    targetName: feedbackData.name
  });
};

// Logs de Filiais
export const logBranchCreate = (user, branchData) => {
  return logAction({
    action: 'CRIAR',
    module: 'Configuracoes',
    type: 'Filiais',
    description: `Nova filial cadastrada: "${branchData.name || 'Sem nome'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: branchData.id,
    targetName: branchData.name,
    metadata: {
      phone: branchData.phone,
      email: branchData.email
    }
  });
};

export const logBranchUpdate = (user, branchData, changes = {}) => {
  return logAction({
    action: 'ATUALIZAR',
    module: 'Configuracoes',
    type: 'Filiais',
    description: `Filial atualizada: "${branchData.name || 'Sem nome'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: branchData.id,
    targetName: branchData.name,
    metadata: { phone: branchData.phone, email: branchData.email, ...changes }
  });
};

export const logBranchDelete = (user, branchData) => {
  return logAction({
    action: 'EXCLUIR',
    module: 'Configuracoes',
    type: 'Filiais',
    description: `Filial excluída: "${branchData.name || 'Sem nome'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: branchData.id,
    targetName: branchData.name
  });
};

// Logs de Propriedades - Status
export const logPropertyStatusUpdate = (user, propertyData, newStatus, oldStatus = null) => {
  const previous = oldStatus ?? propertyData.status;
  const changeDiff = buildSingleChangeDiff('status', 'Status', previous, newStatus);
  return logAction({
    action: 'ATUALIZAR',
    module: 'Propriedades',
    type: 'Propriedades',
    description: `Status do imóvel "${propertyData.title || propertyData.id}" alterado para "${newStatus}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: propertyData.id,
    targetName: propertyData.title,
    metadata: {
      oldStatus: previous,
      newStatus,
      changeDiff
    }
  });
};

export const logPropertyBulkStatusUpdate = (user, count, newStatus) => {
  return logAction({
    action: 'ATUALIZAR',
    module: 'Propriedades',
    type: 'Propriedades',
    description: `Status de ${count} imóvel(is) alterado(s) para "${newStatus}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    metadata: {
      count,
      newStatus
    }
  });
};

export const logPropertyBulkAction = (user, action, count) => {
  return logAction({
    action: 'ATUALIZAR',
    module: 'Propriedades',
    type: 'Propriedades',
    description: `Ação em lote "${action}" aplicada a ${count} imóvel(is)`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    metadata: {
      bulkAction: action,
      count
    }
  });
};

// Logs de Condomínios
export const logCondominiumStatusUpdate = (user, condominiumData, newStatus, oldStatus = null) => {
  const previous = oldStatus ?? condominiumData.status;
  const changeDiff = buildSingleChangeDiff('status', 'Status', previous, newStatus);
  return logAction({
    action: 'ATUALIZAR',
    module: 'Condominios',
    type: 'Condominios',
    description: `Status do condomínio "${condominiumData.title || condominiumData.id}" alterado para "${newStatus}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: condominiumData.id,
    targetName: condominiumData.title,
    metadata: { oldStatus: previous, newStatus, changeDiff }
  });
};

export const logCondominiumBulkStatusUpdate = (user, count, newStatus) => {
  return logAction({
    action: 'ATUALIZAR',
    module: 'Condominios',
    type: 'Condominios',
    description: `Status de ${count} condomínio(s) alterado(s) para "${newStatus}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    metadata: { count, newStatus }
  });
};

export const logCondominiumCreate = (user, condominiumData) => {
  const typeLabel = PROPERTY_TYPE_LABELS[condominiumData.type] || condominiumData.type || 'N/A';
  return logAction({
    action: 'CRIAR',
    module: 'Condominios',
    type: 'Condominios',
    description: `Novo condomínio cadastrado: "${condominiumData.title || 'Sem título'}" (${typeLabel})`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: condominiumData.id,
    targetName: condominiumData.title,
    metadata: {
      type: typeLabel,
      status: deriveCondominiumStatusLabel(condominiumData),
      city: condominiumData.city || '(sem cidade)'
    }
  });
};

export const logCondominiumDuplicate = (user, sourceCondominium, newCondominium) => {
  const typeLabel = PROPERTY_TYPE_LABELS[newCondominium.type] || newCondominium.type || 'N/A';
  return logAction({
    action: 'CRIAR',
    module: 'Condominios',
    type: 'Condominios',
    description: `Condomínio duplicado: "${newCondominium.title || 'Sem título'}" a partir de "${sourceCondominium.title || sourceCondominium.id}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: newCondominium.id,
    targetName: newCondominium.title,
    metadata: {
      sourceId: sourceCondominium.id,
      sourceTitle: sourceCondominium.title,
      type: typeLabel
    }
  });
};

export const logCondominiumUpdate = (user, condominiumData, changes = {}) => {
  return logAction({
    action: 'ATUALIZAR',
    module: 'Condominios',
    type: 'Condominios',
    description: `Condomínio atualizado: "${condominiumData.title || 'Sem título'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: condominiumData.id,
    targetName: condominiumData.title,
    metadata: { ...changes }
  });
};

export const logCondominiumDelete = (user, condominiumData) => {
  return logAction({
    action: 'EXCLUIR',
    module: 'Condominios',
    type: 'Condominios',
    description: `Condomínio excluído: "${condominiumData.title || condominiumData.id}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: condominiumData.id,
    targetName: condominiumData.title
  });
};

export const logCondominiumBulkDelete = (user, count) => {
  return logAction({
    action: 'EXCLUIR',
    module: 'Condominios',
    type: 'Condominios',
    description: `${count} condomínio(s) excluído(s)`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    metadata: { count }
  });
};

export const logCondominiumBulkAction = (user, action, count) => {
  return logAction({
    action: 'ATUALIZAR',
    module: 'Condominios',
    type: 'Condominios',
    description: `Ação em lote "${action}" aplicada a ${count} condomínio(s)`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    metadata: {
      bulkAction: action,
      count
    }
  });
};

// Logs de Landing Pages
export const logLandingPageCreate = (user, pageData) => {
  return logAction({
    action: 'CRIAR',
    module: 'LandingPages',
    type: 'LandingPages',
    description: `Nova página de destino criada: "${pageData.title || 'Sem título'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: pageData.id,
    targetName: pageData.title,
    metadata: { status: translateLandingStatus(pageData.status) }
  });
};

export const logLandingPageUpdate = (user, pageData, changes = {}) => {
  return logAction({
    action: 'ATUALIZAR',
    module: 'LandingPages',
    type: 'LandingPages',
    description: `Página de destino atualizada: "${pageData.title || 'Sem título'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: pageData.id,
    targetName: pageData.title,
    metadata: { status: translateLandingStatus(pageData.status), ...changes }
  });
};

export const logLandingPageDelete = (user, pageData) => {
  return logAction({
    action: 'EXCLUIR',
    module: 'LandingPages',
    type: 'LandingPages',
    description: `Página de destino excluída: "${pageData.title || 'Sem título'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: pageData.id,
    targetName: pageData.title
  });
};

export const logLandingPageStatusChange = (user, pageData, newStatus, oldStatus = null) => {
  const previous = oldStatus ?? pageData.status;
  const changeDiff = buildSingleChangeDiff(
    'status',
    'Status',
    translateLandingStatus(previous),
    translateLandingStatus(newStatus)
  );
  return logAction({
    action: 'ATUALIZAR',
    module: 'LandingPages',
    type: 'LandingPages',
    description: `Status da página de destino "${pageData.title || 'Sem título'}" alterado para "${translateLandingStatus(newStatus)}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: pageData.id,
    targetName: pageData.title,
    metadata: {
      oldStatus: translateLandingStatus(previous),
      newStatus: translateLandingStatus(newStatus),
      changeDiff
    }
  });
};

// Logs de Financeiro
export const logFinancialEntryCreate = (user, entryData) => {
  return logAction({
    action: 'CRIAR',
    module: 'Financeiro',
    type: 'Lancamentos',
    description: `Novo lançamento financeiro criado: "${entryData.entryCode || entryData.description || 'Sem código'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: entryData.id,
    targetName: entryData.entryCode || entryData.description,
    metadata: {
      type: entryData.type,
      value: entryData.value,
      propertyName: entryData.propertyName
    }
  });
};

export const logFinancialEntryUpdate = (user, entryData, changes = {}) => {
  return logAction({
    action: 'ATUALIZAR',
    module: 'Financeiro',
    type: 'Lancamentos',
    description: `Lançamento financeiro atualizado: "${entryData.entryCode || entryData.description || 'Sem código'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: entryData.id,
    targetName: entryData.entryCode || entryData.description,
    metadata: {
      type: entryData.type,
      value: entryData.value,
      ...changes
    }
  });
};

export const logFinancialEntryDelete = (user, entryData) => {
  return logAction({
    action: 'EXCLUIR',
    module: 'Financeiro',
    type: 'Lancamentos',
    description: `Lançamento financeiro excluído: "${entryData.entryCode || entryData.description || 'Sem código'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: entryData.id,
    targetName: entryData.entryCode || entryData.description
  });
};

export const logFinancialEntryDuplicate = (user, entryData) => {
  return logAction({
    action: 'CRIAR',
    module: 'Financeiro',
    type: 'Lancamentos',
    description: `Lançamento financeiro duplicado a partir de: "${entryData.entryCode || 'Sem código'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: entryData.id,
    targetName: entryData.entryCode
  });
};

export const logFinancialEntryStatusChange = (user, entryData, newStatus, oldStatus = null) => {
  const previous = oldStatus ?? entryData.status;
  const changeDiff = buildSingleChangeDiff('status', 'Status', previous, newStatus);
  return logAction({
    action: 'ATUALIZAR',
    module: 'Financeiro',
    type: 'Lancamentos',
    description: `Lançamento "${entryData.entryCode || 'Sem código'}" marcado como "${newStatus}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: entryData.id,
    targetName: entryData.entryCode,
    metadata: { oldStatus: previous, newStatus, changeDiff }
  });
};

// Logs de Agenda - Tarefas
export const logAgendaTaskCreate = (user, taskData) => {
  return logAction({
    action: 'CRIAR',
    module: 'Agenda',
    type: 'Tarefas',
    description: taskData.count > 1
      ? `${taskData.count} tarefas criadas (recorrentes): "${taskData.title || 'Sem título'}"`
      : `Nova tarefa criada: "${taskData.title || 'Sem título'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: taskData.id || null,
    targetName: taskData.title,
    metadata: {
      priority: AGENDA_PRIORITY_LABELS[taskData.priority] || taskData.priority,
      date: taskData.date,
      count: taskData.count || 1
    }
  });
};

export const logAgendaTaskUpdate = (user, taskData, changes = {}) => {
  return logAction({
    action: 'ATUALIZAR',
    module: 'Agenda',
    type: 'Tarefas',
    description: `Tarefa atualizada: "${taskData.title || 'Sem título'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: taskData.id,
    targetName: taskData.title,
    metadata: { ...changes }
  });
};

export const logAgendaTaskDelete = (user, taskData) => {
  return logAction({
    action: 'EXCLUIR',
    module: 'Agenda',
    type: 'Tarefas',
    description: `Tarefa excluída permanentemente: "${taskData.title || 'Sem título'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: taskData.id,
    targetName: taskData.title
  });
};

export const logAgendaTaskTrash = (user, taskData) => {
  return logAction({
    action: 'ATUALIZAR',
    module: 'Agenda',
    type: 'Tarefas',
    description: `Tarefa movida para a lixeira: "${taskData.title || 'Sem título'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: taskData.id,
    targetName: taskData.title
  });
};

export const logAgendaTaskToggle = (user, taskData, completed) => {
  const changeDiff = buildSingleChangeDiff(
    'completed',
    'Concluída',
    completed ? 'Não' : 'Sim',
    completed ? 'Sim' : 'Não'
  );
  return logAction({
    action: 'ATUALIZAR',
    module: 'Agenda',
    type: 'Tarefas',
    description: `Tarefa "${taskData.title || 'Sem título'}" marcada como ${completed ? 'concluída' : 'pendente'}`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: taskData.id,
    targetName: taskData.title,
    metadata: { completed, changeDiff }
  });
};

// Logs de Agenda - Eventos
export const logAgendaEventDelete = (user, eventData) => {
  return logAction({
    action: 'EXCLUIR',
    module: 'Agenda',
    type: 'Eventos',
    description: `Evento excluído: "${eventData.title || 'Sem título'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: eventData.id,
    targetName: eventData.title
  });
};

// Logs de Agenda - Notas
export const logAgendaNoteCreate = (user, noteData) => {
  return logAction({
    action: 'CRIAR',
    module: 'Agenda',
    type: 'Notas',
    description: `Nova nota criada: "${noteData.title || 'Sem título'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: noteData.id,
    targetName: noteData.title
  });
};

export const logAgendaNoteUpdate = (user, noteData, changes = {}) => {
  return logAction({
    action: 'ATUALIZAR',
    module: 'Agenda',
    type: 'Notas',
    description: `Nota atualizada: "${noteData.title || 'Sem título'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: noteData.id,
    targetName: noteData.title,
    metadata: { ...changes }
  });
};

export const logAgendaNoteDelete = (user, noteData) => {
  return logAction({
    action: 'EXCLUIR',
    module: 'Agenda',
    type: 'Notas',
    description: `Nota excluída permanentemente: "${noteData.title || 'Sem título'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: noteData.id,
    targetName: noteData.title
  });
};

export const logAgendaNoteTrash = (user, noteData) => {
  return logAction({
    action: 'ATUALIZAR',
    module: 'Agenda',
    type: 'Notas',
    description: `Nota movida para a lixeira: "${noteData.title || 'Sem título'}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    targetId: noteData.id,
    targetName: noteData.title
  });
};

// Logs de LinkBio
export const logLinkBioCreate = (user) => {
  return logAction({
    action: 'CRIAR',
    module: 'LinkBio',
    type: 'LinkBio',
    description: 'Página Bio criada',
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email
  });
};

export const logLinkBioUpdate = (user, changes = {}) => {
  return logAction({
    action: 'ATUALIZAR',
    module: 'LinkBio',
    type: 'LinkBio',
    description: 'Página Bio atualizada',
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    metadata: { ...changes }
  });
};

export const logLinkBioStatusChange = (user, newStatus, oldStatus = null) => {
  const statusLabel = (s) => (s === 'published' ? 'Publicada' : s === 'paused' ? 'Pausada' : 'Rascunho');
  const previous = oldStatus ?? 'draft';
  const changeDiff = buildSingleChangeDiff('status', 'Status', statusLabel(previous), statusLabel(newStatus));
  return logAction({
    action: 'ATUALIZAR',
    module: 'LinkBio',
    type: 'LinkBio',
    description: `Página Bio alterada para: "${statusLabel(newStatus)}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    metadata: { oldStatus: previous, newStatus, changeDiff }
  });
};

// Logs de Mensagens do CRM/Lead
export const logLeadMessagesUpdate = (user, stage, changes = {}) => {
  return logAction({
    action: 'ATUALIZAR',
    module: 'Configuracoes',
    type: 'Mensagens CRM',
    description: `Mensagens do pipeline atualizadas para o estágio: "${stage}"`,
    userId: user.uid,
    userName: user.displayName || user.name || user.email,
    userEmail: user.email,
    metadata: { stage, ...changes }
  });
};

export default logAction;

